import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonList, PEN_BASIC } from '../..'
import { penTypes } from '../../../Definitions/valueTypes'
import { colors, css, glyphs } from '../../../Styles'

import { ButtonTypes } from '../../../Definitions'
import { BaseSection } from './_Section/interface'
import getBorderRadius from '../../_Components/getBorderRadius'

export enum SectionBasicTypes {
  basicPen = 2,
  advancedPen = 3,
  singlePen = 1,
  selectablePen = 4
}

interface SectionBasic_Props extends BaseSection{
  sectionType: SectionBasicTypes
  selectFilter?: boolean
  selectedPenIds?: number[];
  onPenClick?: (penId: number) => void
  onAddBtn?: () => void
  onMoveBtn?: () => void
  onEditBtn?: () => void
}
/**
 * SECTION_BASIC
 * @param sectionId Unique identifier for each section.
 * @param sectionType Basic, advanced and single. Each renders different content of animal data.
 * @param onPenClick OnClick event to handle penId.
 * @param onAddBtn OnClick event to handle Add btn.
 * @param onMoveBtn OnClick event to handle Move btn.
 * @param onEditBtn OnClick event to handle Edit btn.
 * @returns a section of pens for Freeda Location Feeder systems.
 */
export default function SECTION_BASIC({ sectionId, pens, sectionType, selectedPenIds, onPenClick, onAddBtn, onMoveBtn, onEditBtn }: SectionBasic_Props) {
  const {t} = useTranslation()
  const [selectedPenNo, setSelectedPenNo] = useState<number | null>(null);

  function AddBtnClick() {
    if (onAddBtn) onAddBtn()
  }

  function MoveBtnClick() {
    if (onMoveBtn) onMoveBtn()
  }

  function EditBtnClick() {
    if (onEditBtn) onEditBtn()
  }

  // Handle Pen click
  const handlePenClick = (penId: number) => {
    if (onPenClick) {
      onPenClick(penId);
      setSelectedPenNo(penId)
    }
  };

  const maxRowsAndCols = pens.reduce(
    (acc, pen) => {
      const [endRow, endCol] = pen.visual ? pen.visual.split('/').map(Number) : [0, 0];
      return {
        rows: Math.max(acc.rows, endRow),
        cols: Math.max(acc.cols, endCol),
      };
    },
    { rows: 0, cols: 0 }
  );

  const _gridTemplate = 
    (sectionType === SectionBasicTypes.basicPen) ? "repeat(2, 1fr)"
      : (sectionType===SectionBasicTypes.selectablePen) ? `repeat(${maxRowsAndCols.cols}, 1fr)`
    : sectionType===SectionBasicTypes.advancedPen || sectionType===SectionBasicTypes.singlePen ? "auto"
    : "none"

  const advancedBtns = 
    sectionType===SectionBasicTypes.advancedPen ?
      <ButtonList 
        contents={[t("buttons.add", {defaultValue:"add"}), t("buttons.move", {defaultValue:"move"}), t("buttons.edit", {defaultValue:"edit"})]}
        onClick={(i => {
          if (i === 0) {AddBtnClick()}
          else if (i === 1) {MoveBtnClick()}
          else if (i === 2) {EditBtnClick()}
        })}
        buttonType={ButtonTypes.tableButton}
        startEnhancers={[glyphs.Add({fillColor: colors.greyDarkPrimary}), glyphs.AnimalMove({fillColor: colors.greyDarkPrimary}), glyphs.Edit({fillColor: colors.greyDarkPrimary})]}
        overrides={{
          baseButton: {backgroundColor: colors.greyLightPrimary},
          contentStyles: {color: colors.greyDarkPrimary},
          hoverStyles: {backgroundColor: colors.greyLightSecondary}
        }}
      />
    : <></>

  const columnHeaders = 
    sectionType===SectionBasicTypes.advancedPen ?
      <p style={{ ...css.penHeaders, paddingLeft: "6px" }}>
        {t("locationFeederPages.sections.penValueHeaders", {defaultValue:"Hold the mouse on a value to see column type"})}
      </p>
    : <></>

  // Error throwback if there are no pens
  if (pens.length === 0) {
    return <p style={{...css.paragraph, ...css.fontSize4, color: colors.greyDarkPrimary}}>{t("noData", {defaultValue:"no data"})}</p>;
  }

  const renderContent = () => {
    // const numColumns = sectionType; // Dynamisk kolonneantal
    const columns =
    sectionType === SectionBasicTypes.basicPen
        ? 2
      : sectionType === SectionBasicTypes.selectablePen ? maxRowsAndCols.cols
      : sectionType === SectionBasicTypes.advancedPen || sectionType === SectionBasicTypes.singlePen
      ? 1
      : 1;

    const rows = Math.ceil(pens.length / columns);
  
    return pens.map((pen, index) => {
      const isSelected = selectedPenIds?.includes(pen.location_id) ?? false;
      const row = Math.floor(index / columns);
      const col = index % columns;

      const borderRadius = getBorderRadius({ row, col, columns, rows });
  
      switch (sectionType) {
        case SectionBasicTypes.basicPen:
        case SectionBasicTypes.singlePen:
          return (
            <PEN_BASIC
              key={pen.location_id}
              pen={pen}
              penType={penTypes.basic}
              hasHoverEffect
              borderRadius={{ borderRadius }}
            />
          );
  
        case SectionBasicTypes.advancedPen:
          return (
            <PEN_BASIC
              key={pen.location_id}
              pen={pen}
              penType={penTypes.advanced}
              hasHoverEffect
              onClick={() => handlePenClick(pen?.location_id)}
              borderRadius={{ borderRadius }}
              contentCursor={{cursor: "pointer"}}
            />
          );

        case SectionBasicTypes.selectablePen:
          return (
            <PEN_BASIC
              key={pen.location_id}
              pen={pen}
              penType={penTypes.penName}
              hasHoverEffect
              hasSelectEffect
              selectable
              onClick={() => handlePenClick(pen?.location_id)}
              borderRadius={{ borderRadius }}
              selectFilter={isSelected}
              gridArea={pen.visual}
            />
          );
  
        default:
          return null;
      }
    });
  };
  
  return (
      <div style={{ width: "fit-content", height: "fit-content" }}>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "end", padding: "0px 20px" }}>
          {columnHeaders}
          {advancedBtns}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: _gridTemplate, gridTemplateRows: `repeat(${maxRowsAndCols.rows}, auto)`, width: "fit-content", gap: "10px", borderRadius: "10px" }}>
          {renderContent()}
        </div>
      </div>
  );
}